import { ECRUDFormLayoutType, ICRUDFormDiMetadata, ICRUDFormField, IFormSection } from "@kuika/kuika-cl-model";
import { useEffect, useState } from "react";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaDrawer from "../../../ui-kit/kuika-drawer/kuika-drawer";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";

/** The responsive breakpoint whose layout is being edited/previewed. */
type DeviceKey = "desktop" | "tablet" | "mobile";

export interface IFormsTabLayoutDrawerProps {
  isOpen: boolean;
  metadata: ICRUDFormDiMetadata;
  onClose: () => void;
  onSave: (updated: ICRUDFormDiMetadata) => void;
}

interface IDeviceOption {
  key: DeviceKey;
  label: string;
  icon: string;
}

const DEVICES: IDeviceOption[] = [
  { key: "desktop", label: "Desktop", icon: "device-desktop" },
  { key: "tablet", label: "Tablet", icon: "device-tablet" },
  { key: "mobile", label: "Mobile", icon: "device-mobile" }
];

interface IColumnOption {
  value: ECRUDFormLayoutType;
  label: string;
  icon: string;
}

const COLUMN_OPTIONS: IColumnOption[] = [
  { value: ECRUDFormLayoutType.SingleColumn, label: "1 col", icon: "single-column" },
  { value: ECRUDFormLayoutType.TwoColumn, label: "2 col", icon: "two-columns" },
  { value: ECRUDFormLayoutType.FourColumn, label: "4 col", icon: "four-columns" }
];

/** The grid column count a layout type renders as. */
const layoutColumns = (layout: ECRUDFormLayoutType): number => {
  if (layout === ECRUDFormLayoutType.FourColumn) return 4;
  if (layout === ECRUDFormLayoutType.TwoColumn) return 2;
  return 1;
};

/**
 * "Form Layout" drawer: pick each section's column layout (1 / 2 / 4 col) per responsive breakpoint
 * (Desktop / Tablet / Mobile) with a live preview of the section's enabled fields reflowing. Edits a
 * working copy of the step's form metadata; SAVE commits it back, CANCEL discards. Mobile is 1-col only.
 */
const FormsTabLayoutDrawer = ({ isOpen, metadata, onClose, onSave }: IFormsTabLayoutDrawerProps) => {
  const [working, setWorking] = useState<ICRUDFormDiMetadata | null>(null);
  const [device, setDevice] = useState<DeviceKey>("desktop");

  // Clone the metadata fresh every time the drawer opens so edits stay local until SAVE.
  useEffect((): void => {
    if (isOpen) {
      setWorking(structuredClone(metadata));
      setDevice("desktop");
    }
  }, [isOpen, metadata]);

  const setColumns = (sectionId: string, layout: ECRUDFormLayoutType): void => {
    setWorking((prev: ICRUDFormDiMetadata | null): ICRUDFormDiMetadata | null => {
      if (!prev) return prev;
      const next: ICRUDFormDiMetadata = structuredClone(prev);
      const section: IFormSection | undefined = next.sections.find((s: IFormSection): boolean => s.id === sectionId);
      if (section) section.responsiveLayoutSizes[device] = layout;
      return next;
    });
  };

  const columnOptionsForDevice = (): IColumnOption[] =>
    // Mobile is single-column only; desktop / tablet offer the full set.
    device === "mobile" ? COLUMN_OPTIONS.slice(0, 1) : COLUMN_OPTIONS;

  const renderSection = (section: IFormSection, index: number) => {
    const layout: ECRUDFormLayoutType = section.responsiveLayoutSizes[device];
    const columns: number = device === "mobile" ? 1 : layoutColumns(layout);
    const fields: ICRUDFormField[] = section.items.filter((f: ICRUDFormField): boolean => f.isEnabled);

    return (
      <div className="kuika-wf-forms__layout-section" key={section.id}>
        <div className="kuika-wf-forms__layout-section-head">
          <span className="kuika-wf-forms__layout-section-title">{section.title || `Section ${index + 1}`}</span>
          <KuikaTabs
            className="kuika-wf-forms__layout-cols"
            size="sm"
            value={String(layout)}
            options={columnOptionsForDevice().map((option: IColumnOption) => ({
              value: String(option.value),
              label: (
                <span className="kuika-wf-forms__layout-col-opt">
                  <KuikaSvg name={option.icon} width={14} height={14} />
                  {option.label}
                </span>
              )
            }))}
            onChange={(value: string): void => setColumns(section.id, Number(value) as ECRUDFormLayoutType)}
          />
        </div>

        {fields.length > 0 ? (
          <div
            className="kuika-wf-forms__layout-preview"
            style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
          >
            {fields.map((field: ICRUDFormField) => (
              <span
                className={`kuika-wf-forms__layout-field${
                  field.isReadonlyField ? " kuika-wf-forms__layout-field--readonly" : ""
                }`}
                key={field.id}
              >
                <span className="kuika-wf-forms__layout-field-label">{field.name || "Field"}</span>
                <span className="kuika-wf-forms__layout-field-input" />
              </span>
            ))}
          </div>
        ) : (
          <div className="kuika-wf-forms__layout-empty">
            <KuikaSvg name="search" width={16} height={16} color="var(--dark-8)" />
            No fields added yet
          </div>
        )}
      </div>
    );
  };

  return (
    <KuikaDrawer
      isOpen={isOpen}
      onClose={onClose}
      width={520}
      placement="right"
      header={<span className="kuika-wf-forms__layout-header">FORM LAYOUT</span>}
      footer={
        <span className="kuika-wf-forms__layout-actions">
          <KuikaButton type="text" uppercase onClick={onClose} data-testid="processFormsLayoutCancel">
            Cancel
          </KuikaButton>
          <KuikaButton
            type="primary"
            uppercase
            onClick={(): void => {
              if (working) onSave(working);
            }}
            data-testid="processFormsLayoutSave"
          >
            Save
          </KuikaButton>
        </span>
      }
    >
      <div className="kuika-wf-forms__layout" data-testid="processFormsLayoutDrawer">
        <KuikaTabs
          className="kuika-wf-forms__layout-devices"
          size="lg"
          value={device}
          options={DEVICES.map((d: IDeviceOption) => ({
            value: d.key,
            label: (
              <span className="kuika-wf-forms__layout-device">
                <KuikaSvg name={d.icon} width={14} height={14} />
                {d.label}
              </span>
            )
          }))}
          onChange={(value: string): void => setDevice(value as DeviceKey)}
        />
        {(working?.sections ?? []).map((section: IFormSection, index: number) => renderSection(section, index))}
      </div>
    </KuikaDrawer>
  );
};

export default FormsTabLayoutDrawer;
